import { OptionType } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { DisplayCurrencyType } from '@fullstory/proto/fs/services/webber/frontend/display';
import { FormatNumberOptions } from 'react-intl';

export const currencyFromCurrencyType: { [key in DisplayCurrencyType]: string } = {
  [DisplayCurrencyType.CURRENCY_USD]: 'USD',
  [DisplayCurrencyType.CURRENCY_EUR]: 'EUR',
  [DisplayCurrencyType.CURRENCY_GBP]: 'GBP',
  [DisplayCurrencyType.CURRENCY_YEN]: 'JPY',
  [DisplayCurrencyType.CURRENCY_AED]: 'AED',
  [DisplayCurrencyType.CURRENCY_AUD]: 'AUD',
  [DisplayCurrencyType.CURRENCY_BRL]: 'BRL',
  [DisplayCurrencyType.CURRENCY_CAD]: 'CAD',
  [DisplayCurrencyType.CURRENCY_CLP]: 'CLP',
  [DisplayCurrencyType.CURRENCY_ILS]: 'ILS',
  [DisplayCurrencyType.CURRENCY_INR]: 'INR',
  [DisplayCurrencyType.CURRENCY_MXN]: 'MXN',
  [DisplayCurrencyType.CURRENCY_THB]: 'THB',
  [DisplayCurrencyType.CURRENCY_TRY]: 'TRY',
  [DisplayCurrencyType.CURRENCY_CHF]: 'CHF',
  [DisplayCurrencyType.CURRENCY_SEK]: 'SEK',
  [DisplayCurrencyType.CURRENCY_INVALID]: 'USD',
};

export const localeForCurrencyType: { [key: string]: string } = {
  AED: 'ar-AR',
  CLP: 'es-CL',
  INR: 'en-IN',
  THB: 'th-TH',
  TRY: 'tr-TR',
};

/**
 * Overrides the current local to render the currency value. Certain currencies
 * like AED or INR have both special characters and formatting that will not
 * come through on the default locale. This overrides the values to have them
 * render as expected.
 *
 * The interface here is similar to intl's formatNumber
 *
 * @param value number to format
 * @param opts number formatting options
 * @returns a formatted currency with proper locale
 */
export const formatCurrencyWithLocale = (value: number, opts: FormatNumberOptions) => {
  const locale = localeForCurrencyType[opts.currency ?? ''];
  if (locale) {
    return intl.formatters.getNumberFormat(locale, opts).format(value);
  }

  return intl.formatNumber(value, opts);
};

const formatCurrencyToParts = (value: number, opts: FormatNumberOptions) => {
  const locale = localeForCurrencyType[opts.currency ?? ''];
  if (locale) {
    return intl.formatters.getNumberFormat(locale, opts).formatToParts(value);
  }

  return intl.formatNumberToParts(value, opts);
};

/**
 * Get the currency symbol and index given the current locale.
 * Not all locales use $0.00 as the position of the currency symbol,
 * this method will return the symbol and the position of the symbol.
 *
 * @param currencyCode USD, JPY, CAD, etc
 * @returns object containing symbol and index of the symbol
 */
export const getCurrencySymbol = (currencyCode: string) => {
  const parts = formatCurrencyToParts(0, {
    style: 'currency',
    currency: currencyCode,
  });

  let symbol = '';
  let index = 0;
  let literal = '';

  for (let i = 0; i < parts.length; i++) {
    if (parts[i]?.type === 'currency') {
      symbol = parts[i]!.value;
      index = i;
    }
    // some currencies have UTF empty whitespace literals we should ignore
    if (parts[i]?.type === 'literal' && !!parts[i]?.value && parts[i]?.value !== '‏') {
      literal = parts[i]!.value;
    }
  }
  return {
    symbol,
    index,
    literal,
  };
};

/**
 * Returns a fallback string given the currency code and current locale.
 * For most non-english countries, the position of the currency follows
 * after the value with a space.
 *
 * @param currencyCode USD, JPY, CAD, etc
 * @returns string with a "proper" positioned fallback $-- or -- $
 */
export const getCurrencyPlaceholder = (currencyCode: string) => {
  const { symbol, index, literal } = getCurrencySymbol(currencyCode);
  return index > 0
    ? intl.formatMessage(
        {
          defaultMessage: '--{literal}{currencySymbol}',
          id: '7S7B+X',
          description: 'Used to signify that there is no currency value. e.g -- $',
        },
        { currencySymbol: symbol, literal },
      )
    : intl.formatMessage(
        {
          defaultMessage: '{currencySymbol}{literal}--',
          id: 'bsup7i',
          description: 'Used to signify that there is no currency value. e.g $--',
        },
        { currencySymbol: symbol, literal },
      );
};

/**
 * Helper method to return an intl'd string of the currency code and symbol
 * e.g. USD ($)
 * @param currency
 * @returns intl'd string of currency code and symbol e.g. USD ($)
 */
export const getCurrencyString = (currency: DisplayCurrencyType) => {
  const currencyCode = currencyFromCurrencyType[currency];
  const { symbol } = getCurrencySymbol(currencyCode);
  return intl.formatMessage(
    {
      defaultMessage: '{currencyCode} ({symbol})',
      id: '9pmwyl',
      description: 'The currency code and symbol e.g. USD ($)',
    },
    { currencyCode, symbol },
  );
};

/**
 * Returns currency values as an OptionType array for use with SelectMenu.
 * The label returned is like: "USD ($)".
 * @param includeNewCurrencies ignore currencies added after CURRENCY_YEN
 * @returns a list of currency options
 */
export const getCurrencyOptions = (): OptionType<DisplayCurrencyType>[] => {
  const opts: OptionType<DisplayCurrencyType>[] = [];
  for (const key of Object.values(DisplayCurrencyType)) {
    if (!isNaN(parseInt(key.toString(), 10)) && (key as DisplayCurrencyType)) {
      const currency = key as DisplayCurrencyType;
      const currencyCode = currencyFromCurrencyType[currency];

      opts.push({
        key: currencyCode,
        label: getCurrencyString(currency),
        value: currency,
      });
    }
  }

  return opts;
};
